import React, { useEffect } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import * as LuIcons from 'react-icons/lu';
import SafeIcon from '../common/SafeIcon';

const { LuCheckCircle, LuXCircle, LuInfo } = LuIcons;

export type ToastType = 'success' | 'error' | 'info';

export interface ToastMessage {
  id: string;
  type: ToastType;
  message: string;
}

interface ToastProps {
  toasts: ToastMessage[];
  removeToast: (id: string) => void;
}

export function ToastContainer({ toasts, removeToast }: ToastProps) {
  return (
    <div className="fixed bottom-4 right-4 z-50 flex flex-col gap-2 pointer-events-none">
      <AnimatePresence>
        {toasts.map((toast) => (
          <Toast key={toast.id} toast={toast} removeToast={removeToast} />
        ))}
      </AnimatePresence>
    </div>
  );
}

function Toast({ toast, removeToast }: { toast: ToastMessage; removeToast: (id: string) => void; key?: React.Key }) {
  useEffect(() => {
    const timer = setTimeout(() => {
      removeToast(toast.id);
    }, 3000);
    return () => clearTimeout(timer);
  }, [toast.id, removeToast]);

  const icons = {
    success: <SafeIcon icon={LuCheckCircle} className="w-5 h-5 text-green-500" />,
    error: <SafeIcon icon={LuXCircle} className="w-5 h-5 text-red-500" />,
    info: <SafeIcon icon={LuInfo} className="w-5 h-5 text-blue-500" />
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20, scale: 0.9 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={{ opacity: 0, scale: 0.9 }}
      className="bg-white px-4 py-3 rounded-xl shadow-lg border border-slate-100 flex items-center gap-3 pointer-events-auto"
    >
      {icons[toast.type]}
      <span className="text-sm font-medium text-slate-700">{toast.message}</span>
    </motion.div>
  );
}